// ============================================================
// LoginScreen.tsx
// Mobile-responsive login screen
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ErrorNote } from '../shared/ActionBox'
import { useCasevoStore } from '../../store/useCasevoStore'
import { loginApi, verify2faApi, getMeApi } from '../../api/casevoApi'
interface LoginScreenProps {
  // Wired up in Phase 4 (Forgot Password) — the prop is accepted now
  // so App.tsx compiles, but the "Forgot password?" link isn't added
  // to the UI yet.
  onShowRequestReset: () => void
}
// Per-field validation errors. The single banner above the form is now
// reserved for server-side errors only (e.g. "Invalid email or password").
type FieldErrors = Partial<Record<'email' | 'password', string>>
const LoginScreen: React.FC<LoginScreenProps> = ({ onShowRequestReset }) => {
  const { setAuth, setAuthUser } = useCasevoStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  // 2FA step. When login returns { twoFactorRequired, preAuthToken } we
  // hold the (short-lived, 5-min) pre-auth token here and swap the form
  // for a code-entry view. null = normal password step. `code` is the
  // 6-digit TOTP OR a backup code.
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null)
  const [code, setCode] = useState('')
  // Empty until the user submits with invalid data — we deliberately do not
  // pre-flag fields on first render.
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // Focus the email field as soon as the screen mounts so the user can
  // start typing without a click. Empty deps array → runs once.
  const emailRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    emailRef.current?.focus()
  }, [])
  // Runs all client-side rules and returns whichever messages applied.
  // Caller decides what to do with the result (set state vs. proceed).
  const validate = (): FieldErrors => {
    const errs: FieldErrors = {}
    if (!email.trim()) {
      errs.email = 'Please enter your email address'
    } else if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      errs.email = 'Please enter a valid email address'
    }
    if (!password) {
      errs.password = 'Please enter your password'
    }
    return errs
  }
  // Clears one field's error the moment the user starts editing it again.
  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    // Clear any stale server-error banner before re-validating, so the
    // user doesn't see a previous attempt's message ("Invalid email or
    // password") sitting next to a fresh per-field error.
    setError(null)
    // Field-level validation — short-circuit on any failure and surface
    // the messages inline. The single error banner above the form is now
    // reserved for server-side errors only.
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    try {
      setLoading(true)
      setError(null)
      setFieldErrors({})
      const auth = await loginApi(email, password)
      // 2FA branch: password was correct but this account has 2FA on.
      // Do NOT treat as logged in — switch to the code-entry step.
      if (auth.twoFactorRequired) {
        setPreAuthToken(auth.preAuthToken)
        setCode('')
        return
      }
      // Normal login — complete the session exactly as before.
      await completeLogin(auth.token, auth.user)
    } catch (err) {
      // Distinguish "the server responded with an error" from "we couldn't
      // reach the server". axios.isAxiosError is a typed guard so we keep
      // strict typing on err without resorting to `any`.
      if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
        const backendMessage = err.response.data?.error
        if (typeof backendMessage === 'string' && backendMessage.length > 0) {
          // Most informative — the backend's specific message wins.
          setError(backendMessage)
        } else if (err.response.status === 401) {
          // Backend was reached and rejected the credentials, but didn't
          // give us a useful message body.
          setError('Invalid email or password.')
        } else {
          // Any other 4xx/5xx with no actionable message.
          setError('Something went wrong. Please try again.')
        }
      } else {
        // No response received → network failure (offline, DNS, CORS,
        // timeout, server unreachable). Or a non-axios error, treated
        // the same way to be safe.
        setError('Could not connect to the server. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }
  // Shared completion path — used by BOTH a normal login and a successful
  // 2FA verify, so the session is set identically in both. Stores the
  // token + partial user, then best-effort fetches the full profile.
  const completeLogin = async (
    token: string,
    user: Parameters<typeof setAuth>[1],
  ): Promise<void> => {
    setAuth(token, user)
    try {
      const me = await getMeApi()
      setAuthUser(me)
    } catch {
      console.warn('Could not fetch full user profile — continuing with partial data')
    }
  }
  // Submit the 2FA code (TOTP or backup). On success completes the login;
  // on an EXPIRED pre-auth token (5-min window) it drops back to the
  // password step; a wrong code keeps the user on this step to retry.
  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!code.trim()) {
      setError('Please enter your verification code.')
      return
    }
    if (!preAuthToken) return
    try {
      setLoading(true)
      const auth = await verify2faApi(preAuthToken, code.trim())
      await completeLogin(auth.token, auth.user)
    } catch (err) {
      let message = 'Invalid code. Please try again.'
      if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
        const backendMessage = err.response.data?.error
        if (typeof backendMessage === 'string' && backendMessage.length > 0) {
          message = backendMessage
        }
      }
      // An expired/invalid pre-auth token means the whole login must
      // restart — return to the password form with the message. A wrong
      // code (message doesn't mention expiry) leaves them on this step.
      if (/expired/i.test(message)) {
        setPreAuthToken(null)
        setCode('')
        setPassword('')
      }
      setError(message)
    } finally {
      setLoading(false)
    }
  }
  // Base input class — border colour lives in fieldClass() below so we can
  // flip to red without fighting Tailwind's class-order resolution.
  const inputBase = `w-full border rounded-lg px-4 py-3
    text-base bg-casevo-input text-casevo-forest focus:outline-none
    focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed`
  const fieldClass = (key: keyof FieldErrors) =>
    `${inputBase} ${fieldErrors[key] ? 'border-red-500' : 'border-casevo-border'}`
  // Inline spinner — `currentColor` lets it inherit the button's text colour
  // so it works on both the brand-green button and any future variants.
  const Spinner: React.FC = () => (
    <svg
      className="animate-spin h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
      />
    </svg>
  )
  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
      {/* Nav bar */}
      <nav className="bg-casevo-header px-4 sm:px-6 h-14 flex items-center border-b border-casevo-border">
        <div className="flex items-center gap-3">
          <svg width="28" height="28" viewBox="0 0 68 68">
            <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
            <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
            <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
          </svg>
          <div className="flex flex-col">
            <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">Casevo</span>
            <span className="text-casevo-light text-xs tracking-widest uppercase leading-none">Case Management</span>
          </div>
        </div>
      </nav>
      {/* Login form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
          {preAuthToken ? (
            <>
              <div className="mb-8">
                <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Two-step verification</p>
                <h1 className="text-2xl font-medium text-casevo-forest">Enter your code</h1>
                <p className="text-sm text-casevo-text-soft mt-2">
                  Open your authenticator app and enter the 6-digit code. You can
                  also use one of your backup codes.
                </p>
              </div>
              <form onSubmit={handleVerify2fa}>
                {error && (
                  <ErrorNote className="mb-4">{error}</ErrorNote>
                )}
                <div className="mb-6">
                  <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                    Verification code
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    disabled={loading}
                    className={inputBase.replace('disabled:cursor-not-allowed', 'disabled:cursor-not-allowed tracking-widest') + ' border-casevo-border'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {loading && <Spinner />}
                  {loading ? 'Verifying...' : 'Verify'}
                </button>
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => { setPreAuthToken(null); setCode(''); setError(null) }}
                    disabled={loading}
                    className="text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-forest disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            </>
          ) : (
          <>
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Welcome back</p>
            <h1 className="text-2xl font-medium text-casevo-forest">Sign in to Casevo</h1>
          </div>
          <form onSubmit={handleLogin}>
            {error && (
              <ErrorNote className="mb-4">{error}</ErrorNote>
            )}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Email address
              </label>
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  clearFieldError('email')
                }}
                placeholder="you@yourfirm.co.uk"
                className={fieldClass('email')}
                autoComplete="email"
                inputMode="email"
                disabled={loading}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div className="mb-2">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    clearFieldError('password')
                  }}
                  placeholder="••••••••"
                  className={`${fieldClass('password')} pr-12`}
                  autoComplete="current-password"
                  disabled={loading}
                  aria-invalid={!!fieldErrors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-casevo-mid hover:text-casevo-forest text-xs uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.password}</p>
              )}
            </div>
            {/* Forgot password? — type="button" so it doesn't submit the form.
                Lives inside the <form> for visual alignment with the email /
                password fields above; disabled while loading so it can't be
                clicked mid-login. */}
            <div className="text-right mb-6">
              <button
                type="button"
                onClick={onShowRequestReset}
                disabled={loading}
                className="text-xs uppercase tracking-widest text-casevo-mid hover:text-casevo-forest disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              {loading && <Spinner />}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          {/* Invite-only product (24 June): the register-here link was
              removed alongside the backend's permanent-403 on
              /api/auth/register. Accounts are created only via admin
              invite. If a user needs an account, their firm admin
              sends an invite from the admin panel. */}
          </>
          )}
        </div>
      </div>
    </div>
  )
}
export default LoginScreen
